import { useCallback, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch, useAppSelector } from "app/hooks";
import MicrophoneFormField from "components/microphone-form-field";
import SpeakerFormField from "components/speaker-form-field";
import { selectCurrentUser } from "features/auth/auth-slice";
import MicrophoneSetupModal from "features/medical-scribe/medical-scribe-recording-widget/microphone-setup-modal/microphone-setup-modal.component";
import { useMicrophoneSetup } from "features/medical-scribe/medical-scribe-recording-widget/use-microphone-setup.hook";
import {
  changeScribeDeviceSettings,
  selectMedicalScribeDeviceSettings,
  toggleShowDeviceSettingsDialog
} from "features/medical-scribe/medical-scribe.slice";

const checkBoxSx = {
  transform: "translateY(1px)",
  color: "white",
  "&.Mui-checked": {
    color: "white"
  },
  "& .MuiSvgIcon-root": {
    fill: "white"
  }
};

export default function DeviceSettingsModal() {
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectMedicalScribeDeviceSettings);
  const currentUser = useAppSelector(selectCurrentUser);
  const theme = useTheme();

  const { markSetupComplete } = useMicrophoneSetup();
  const [showMicSetupModal, setShowMicSetupModal] = useState(false);

  const [microphone, setMicrophone] = useState<string>(settings.microphone);
  const [speaker, setSpeaker] = useState<string>(settings.speaker);
  const [showTranscriptionPreview, setShowTranscriptionPreview] =
    useState<boolean>(settings.showTranscriptionPreview ?? true);
  const [autoGenerateSOAPNotes, setAutoGenerateSOAPNotes] = useState<boolean>(
    settings.autoGenerateSOAPNotes ?? true
  );
  const [autoFindRelatedChartNote, setAutoFindRelatedChartNote] =
    useState<boolean>(settings.autoFindRelatedChartNote ?? true);

  const handleClose = useCallback(() => {
    dispatch(toggleShowDeviceSettingsDialog());
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    dispatch(
      changeScribeDeviceSettings({
        microphone,
        speaker,
        showTranscriptionPreview,
        autoGenerateSOAPNotes,
        autoFindRelatedChartNote
      })
    );
    handleClose();
  }, [
    dispatch,
    handleClose,
    microphone,
    speaker,
    showTranscriptionPreview,
    autoGenerateSOAPNotes,
    autoFindRelatedChartNote
  ]);

  return (
    <>
      <div
        style={{
          position: "absolute",
          right: 120,
          bottom: 90,
          zIndex: theme.zIndex.snackbar + 1
        }}
      >
        <Box
          sx={{
            maxWidth: "410px",
            background: `hsla(272, 18%, 48%, 0.85)`,
            boxShadow: theme.shadows[3],
            borderRadius: 2
          }}
        >
          <Grid container gap={3} sx={{ pb: 2 }}>
            <Grid item xs={12} mt={2}>
              <Typography ml={2} fontSize="20px" fontWeight={700} color="white">
                Ambient Scribe Settings
              </Typography>
            </Grid>
            <Grid item xs={11}>
              <Stack sx={{ pl: 2 }} rowGap={2}>
                <MicrophoneFormField
                  label="Default microphone"
                  value={microphone}
                  color="white"
                  onChange={(value: string) => {
                    setMicrophone(value);
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setShowMicSetupModal(true)}
                >
                  Set Up Microphone
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={11}>
              <SpeakerFormField
                sx={{ pl: 2 }}
                label="Default speaker"
                value={speaker}
                color="white"
                onChange={(value: string) => {
                  setSpeaker(value);
                }}
              />
            </Grid>

            <Grid item xs={11}>
              <FormControlLabel
                label="Show transcription preview"
                control={
                  <Checkbox
                    sx={checkBoxSx}
                    checked={showTranscriptionPreview}
                    onChange={(e) =>
                      setShowTranscriptionPreview(e.target.checked)
                    }
                  />
                }
                sx={{ pl: 2, color: "white" }}
              />
              <FormControlLabel
                label="Auto generate Scribe"
                control={
                  <Checkbox
                    sx={checkBoxSx}
                    checked={autoGenerateSOAPNotes}
                    onChange={(e) =>
                      setAutoGenerateSOAPNotes(e.target.checked)
                    }
                  />
                }
                sx={{ pl: 2, color: "white", mt: -1 }}
              />
              <FormControlLabel
                label="Auto find related Chart Note"
                control={
                  <Checkbox
                    sx={checkBoxSx}
                    checked={autoFindRelatedChartNote}
                    onChange={(e) =>
                      setAutoFindRelatedChartNote(e.target.checked)
                    }
                  />
                }
                sx={{ pl: 2, color: "white", mt: -1 }}
              />
            </Grid>

            <Grid item xs={12} sx={{ px: 2 }}>
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
              >
                <Button
                  size="small"
                  sx={{
                    mt: 2,
                    width: "max-content",
                    color: "white",
                    border: "solid 1px white"
                  }}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  sx={{ mt: 2, width: "max-content" }}
                  onClick={handleSubmit}
                >
                  Save Changes
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </div>
      {showMicSetupModal && (
        <MicrophoneSetupModal
          onClose={() => setShowMicSetupModal(false)}
          onComplete={(micId) => {
            markSetupComplete();
            setMicrophone(micId);
            dispatch(
              changeScribeDeviceSettings({ ...settings, microphone: micId })
            );
          }}
          currentSettings={{ ...settings, microphone }}
          userId={currentUser?.id ?? 0}
        />
      )}
    </>
  );
}
